import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useChatStore } from '../../store/chat';
import { formatPrice } from '../../lib/format';
import type { parseCotacao } from './CotacaoCard';

type CotacaoParsed = ReturnType<typeof parseCotacao>;

/** Endereço mock (BR) só para a demo do checkout — não vem de nenhum cadastro real. */
const MOCK_ENDERECO = {
  linha1: 'Rua Sergipe, 1250 — Apto 304',
  linha2: 'Savassi, Belo Horizonte — MG',
  cep: '30130-174',
};

const SHARE_URL = typeof window !== 'undefined' ? window.location.origin : 'https://minasbrasil.com.br';
const SHARE_TEXT = `Acabei de fechar meu orçamento manipulado com a Mina, da Farmácia Minas Brasil! Dá uma olhada: ${SHARE_URL}`;

type Stage = 'checkout' | 'endereco' | 'compartilhar';

/**
 * Checkout no estilo WhatsApp FLOW (bottom sheet nativo): alça de arrasto, cabeçalho
 * com título + fechar, corpo com passos (plano → resumo) e CTA fixo embaixo.
 * "Confirmar pedido" envia a escolha como mensagem no chat (a Mina segue o fluxo
 * de pagamento/confirmação) — nada é cobrado na hora. Depois do endereço (mock),
 * a tela vira uma confirmação full-screen convidando a compartilhar com um amigo.
 */
export function FlowSheet({
  cotacao,
  planoInicial,
  onClose,
}: {
  cotacao: CotacaoParsed;
  planoInicial: number;
  onClose: () => void;
}) {
  const { planos, ingredientes, posologia, forma } = cotacao;
  const [dias, setDias] = useState(planoInicial);
  const [stage, setStage] = useState<Stage>('checkout');
  const plano = planos.find((p) => p.dias === dias) ?? planos[0];

  // trava o scroll do chat atrás do sheet + fecha com Esc (desktop)
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  function irParaEndereco() {
    if (!plano) return;
    setStage('endereco');
  }

  function confirmarEndereco() {
    if (!plano) return;
    const qtd = plano.n_unidades && plano.n_unidades > 1 ? ` (${Math.round(plano.n_unidades)} unidades)` : '';
    useChatStore
      .getState()
      .sendMessage(
        `Quero fechar o pedido do plano de ${plano.dias} dias${qtd} por ${formatPrice(plano.preco)}. Pode seguir! ✅`
      );
    setStage('compartilhar');
  }

  function compartilharWhatsapp() {
    window.open(`https://wa.me/?text=${encodeURIComponent(SHARE_TEXT)}`, '_blank', 'noopener,noreferrer');
  }

  if (stage === 'compartilhar') {
    return createPortal(
      <div
        className="fixed inset-0 z-50 flex flex-col items-center justify-center wa-success-gradient wa-fade-enter px-6 text-center"
        role="dialog"
        aria-modal="true"
        aria-label="Pedido confirmado"
      >
        <img src="/assets/logo-mb.svg" alt="Minas Brasil" className="w-20 h-20 drop-shadow-[0_2px_10px_rgba(0,0,0,0.2)] mb-5" />
        <h1 className="text-white text-[24px] font-semibold leading-tight mb-2">Pedido confirmado! 🎉</h1>
        <p className="text-white/90 text-[14.5px] leading-snug max-w-[280px] mb-1">
          Entrega em <strong>{MOCK_ENDERECO.linha1}</strong>, {MOCK_ENDERECO.linha2}.
        </p>
        <p className="text-white/90 text-[14.5px] leading-snug max-w-[300px] mb-8">
          Chama um amigo pra também provar a Minas Brasil — compartilha o link do nosso app!
        </p>

        <button
          type="button"
          onClick={compartilharWhatsapp}
          className="w-full max-w-[300px] flex items-center justify-center gap-2 rounded-full py-3 text-[15px] font-semibold text-[#0B846B] bg-white active:bg-white/90 transition-colors shadow-[0_4px_16px_rgba(0,0,0,0.18)]"
        >
          <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.28-1.39a9.9 9.9 0 0 0 4.76 1.21h.01c5.46 0 9.9-4.45 9.9-9.91C22 6.45 17.5 2 12.04 2zm5.83 14.03c-.24.68-1.4 1.32-1.93 1.4-.5.08-1.11.11-1.79-.11-.41-.13-.95-.31-1.63-.6-2.87-1.24-4.74-4.14-4.89-4.33-.14-.2-1.17-1.56-1.17-2.98s.73-2.11 1-2.4c.24-.27.53-.34.71-.34l.5.01c.16.01.38-.06.6.46.24.57.8 1.99.87 2.13.07.14.12.31.02.5-.09.19-.14.31-.28.48-.14.16-.29.36-.42.48-.14.13-.28.28-.12.55.16.27.7 1.16 1.51 1.88 1.04.93 1.91 1.22 2.19 1.36.27.13.43.11.59-.07.16-.18.68-.79.86-1.06.18-.27.36-.22.6-.13.24.09 1.55.73 1.81.87.27.13.45.2.51.31.07.11.07.63-.17 1.3z" />
          </svg>
          Compartilhar no WhatsApp
        </button>

        <button
          type="button"
          onClick={onClose}
          className="mt-4 text-white/85 text-[13.5px] font-medium underline underline-offset-2"
        >
          Voltar para o chat
        </button>
      </div>,
      document.body
    );
  }

  const sheet = (
    <div className="fixed inset-0 z-50 flex flex-col justify-end" role="dialog" aria-modal="true" aria-label="Fechar pedido">
      {/* backdrop */}
      <button
        type="button"
        aria-label="Fechar"
        onClick={onClose}
        className="absolute inset-0 bg-[#0B141A]/55 wa-fade-enter cursor-default"
      />

      {/* sheet */}
      <div className="relative bg-white rounded-t-[18px] max-h-[92dvh] flex flex-col wa-sheet-enter shadow-[0_-8px_32px_rgba(0,0,0,0.25)]">
        {/* alça */}
        <div className="flex justify-center pt-2.5 pb-1 flex-shrink-0">
          <div className="w-9 h-[4px] rounded-full bg-[#D1D7DB]" />
        </div>

        {/* header */}
        <div className="flex items-center gap-3 px-4 pb-2.5 border-b border-[#F0F2F5] flex-shrink-0">
          {stage === 'endereco' && (
            <button
              type="button"
              aria-label="Voltar"
              onClick={() => setStage('checkout')}
              className="w-8 h-8 -ml-1 flex items-center justify-center rounded-full text-[#54656F] active:bg-[#F0F2F5] transition-colors flex-shrink-0"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[16.5px] font-semibold text-[#111B21] leading-tight">
              {stage === 'endereco' ? 'Endereço de entrega' : 'Fechar pedido'}
            </div>
            <div className="text-[12px] text-[#667781]">Farmácia Minas Brasil · manipulação</div>
          </div>
          <button
            type="button"
            aria-label="Fechar checkout"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-[#54656F] active:bg-[#F0F2F5] transition-colors flex-shrink-0"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        {/* corpo scrollável */}
        {stage === 'endereco' ? (
          <div className="overflow-y-auto px-4 py-3 flex flex-col gap-4">
            <section>
              <h3 className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#667781] mb-2">
                Confirme onde entregar
              </h3>
              <div className="flex items-start gap-3 rounded-[12px] border border-[#E6E9EC] px-3 py-3">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#1DAB61" className="flex-shrink-0 mt-0.5" aria-hidden="true">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" />
                </svg>
                <div className="min-w-0">
                  <div className="text-[14px] font-medium text-[#111B21]">{MOCK_ENDERECO.linha1}</div>
                  <div className="text-[13px] text-[#667781]">{MOCK_ENDERECO.linha2}</div>
                  <div className="text-[13px] text-[#667781]">CEP {MOCK_ENDERECO.cep}</div>
                </div>
              </div>
            </section>

            {plano && (
              <div className="flex items-center justify-between rounded-[12px] bg-[#075E54] text-white px-4 py-3">
                <span className="text-[13px] opacity-90">Total · {plano.dias} dias</span>
                <span className="text-[19px] font-semibold">{formatPrice(plano.preco)}</span>
              </div>
            )}

            <p className="text-[11.5px] text-[#8696A0] leading-snug -mt-1">
              Endereço de exemplo para esta demonstração. Nada é cobrado agora.
            </p>
          </div>
        ) : (
        <div className="overflow-y-auto px-4 py-3 flex flex-col gap-4">
          {/* passo 1: plano */}
          {planos.length > 0 && (
            <section>
              <h3 className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#667781] mb-2">
                1 · Escolha o plano
              </h3>
              <div className="flex flex-col gap-2">
                {planos.map((p) => {
                  const ativo = p.dias === dias;
                  return (
                    <button
                      key={p.dias}
                      type="button"
                      onClick={() => setDias(p.dias)}
                      aria-pressed={ativo}
                      className={`w-full flex items-center gap-3 rounded-[12px] border px-3 py-2.5 text-left transition-colors ${
                        ativo ? 'border-[#1DAB61] bg-[#F0FDF4]' : 'border-[#E6E9EC] bg-white active:bg-[#F5F6F6]'
                      }`}
                    >
                      <span
                        aria-hidden="true"
                        className={`w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          ativo ? 'border-[#1DAB61]' : 'border-[#B8C0C6]'
                        }`}
                      >
                        {ativo && <span className="w-[9px] h-[9px] rounded-full bg-[#1DAB61]" />}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="flex items-center gap-1.5">
                          <span className="text-[14.5px] font-medium text-[#111B21]">{p.dias} dias</span>
                          {p.dias === 90 && (
                            <span className="text-[9.5px] font-bold uppercase bg-[#25D366]/15 text-[#0B846B] rounded-full px-1.5 py-[1px]">
                              mais escolhido
                            </span>
                          )}
                        </span>
                        {p.n_unidades && p.n_unidades > 1 && (
                          <span className="block text-[11.5px] text-[#8696A0]">
                            {Math.round(p.n_unidades)} unidades{p.por_dia ? ` · ${formatPrice(p.por_dia)}/dia` : ''}
                          </span>
                        )}
                      </span>
                      <span className="text-right flex-shrink-0">
                        <span className="block text-[15px] font-semibold text-[#075E54]">{formatPrice(p.preco)}</span>
                        {(p.desconto_pct ?? 0) > 0 && (
                          <span className="block text-[11px] font-medium text-[#0B846B]">-{p.desconto_pct}%</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* passo 2: resumo da fórmula */}
          <section>
            <h3 className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#667781] mb-2">
              2 · Resumo da fórmula
            </h3>
            <div className="rounded-[12px] border border-[#E6E9EC] overflow-hidden">
              {ingredientes.filter((i) => i?.nome).length > 0 ? (
                <ul className="divide-y divide-[#F0F2F5]">
                  {ingredientes
                    .filter((i) => i?.nome)
                    .map((i, idx) => (
                      <li key={idx} className="flex items-baseline justify-between gap-3 px-3 py-2">
                        <span className="text-[13.5px] text-[#111B21] min-w-0 truncate">{i.nome}</span>
                        {i.dose !== undefined && i.dose !== null && (
                          <span className="text-[12.5px] text-[#667781] tabular-nums flex-shrink-0">
                            {i.dose} {i.unidade ?? ''}
                          </span>
                        )}
                      </li>
                    ))}
                </ul>
              ) : (
                <div className="px-3 py-2 text-[13px] text-[#667781]">Fórmula conforme a receita enviada.</div>
              )}
              {(posologia || forma) && (
                <div className="px-3 py-2 bg-[#FAFBFB] border-t border-[#F0F2F5] text-[12px] text-[#667781]">
                  {forma && <span className="capitalize">{forma}</span>}
                  {forma && posologia && ' · '}
                  {posologia}
                </div>
              )}
            </div>
          </section>

          {/* total */}
          {plano && (
            <div className="flex items-center justify-between rounded-[12px] bg-[#075E54] text-white px-4 py-3">
              <span className="text-[13px] opacity-90">Total · {plano.dias} dias</span>
              <span className="text-[19px] font-semibold">{formatPrice(plano.preco)}</span>
            </div>
          )}

          <p className="text-[11.5px] text-[#8696A0] leading-snug -mt-1">
            Nada é cobrado agora — a Mina confirma o pedido e envia as opções de pagamento aqui no chat.
          </p>
        </div>
        )}

        {/* CTA fixo */}
        <div
          className="px-4 pt-2 border-t border-[#F0F2F5] flex-shrink-0 bg-white"
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            onClick={stage === 'endereco' ? confirmarEndereco : irParaEndereco}
            disabled={!plano}
            className="w-full rounded-full py-3 text-[15px] font-semibold text-white bg-[#1DAB61] active:bg-[#179552] disabled:opacity-50 transition-colors shadow-[0_2px_8px_-2px_rgba(0,168,132,0.6)]"
          >
            {stage === 'endereco'
              ? `Confirmar endereço e fechar pedido${plano ? ` · ${formatPrice(plano.preco)}` : ''}`
              : `Confirmar pedido${plano ? ` · ${formatPrice(plano.preco)}` : ''}`}
          </button>
          <div className="flex items-center justify-center gap-1 mt-1.5 text-[10.5px] text-[#8696A0]">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
            </svg>
            Gerenciado pela Mina · Farmácia Minas Brasil
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}
